import { clamp as _clamp } from '@studio/js-utils/range';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { isNil, parseJson, toBoolean, toEnum, toJsonObject, toNumber } from '@tryiris-ai/js-types';
import { Range, Speeds } from './constants.js';
import type { Facet, Rate, Speed } from './types.js';
import { Pace, Tempo } from './types.js';

/**
 * Clamps the specified speed to its minimum/maximum range (`Range`).
 *
 * @param value - Speed value to be clamped.
 * @returns Clamped speed.
 */
export const clamp = (value: number): Speed => _clamp(value, Range);

/**
 * Compute the zoom intent based on the wheel velocity.
 *
 * @param args - Zoom arguments.
 * @param args.wheelSpeed - The wheel speed between `0..1`.
 * @param args.tempo - Zoom tempo.
 * @returns The zoom intent.
 */
export const computeZoom = ({
  tempo,
  rate,
  wheelSpeed
}: {
  tempo: Tempo;
  rate: Rate;
  wheelSpeed: number;
}): number =>
  tempo === Tempo.Fixed ? rate.value * Math.sign(wheelSpeed) : wheelSpeed * rate.value;

/**
 * Return a pace one step faster than the specified pace.
 *
 * @param pace - The pace to be increased.
 * @returns The increased pace.
 */
export const getFasterPace = (pace: Pace): Pace => (pace === Pace.Slow ? Pace.Medium : Pace.Fast);

/**
 * Returns the associated pace for the specified speed.
 *
 * @param facet - The facet whose pace is to be determined.
 * @param speed - The speed whose relative pace is to be determined.
 * @returns The associated pace.
 */
export const getPaceForSpeed = (facet: Facet, speed: Speed): Pace =>
  speed >= Speeds[facet][Pace.Fast]
    ? Pace.Fast
    : speed >= Speeds[facet][Pace.Medium]
      ? Pace.Medium
      : Pace.Slow;

/**
 * Return the pace one step slower than the specified pace.
 *
 * @param pace - Current pace.
 * @returns The decreased pace.
 */
export const getSlowerPace = (pace: Pace): Pace => (pace === Pace.Fast ? Pace.Medium : Pace.Slow);

/**
 * Transforms the specified value to a `Pace` enumeration.
 *
 * @param value - The value to be transformed.
 * @returns The transformed `Pace` enumeration or `undefined`.
 */
export const toPace = (value: unknown): T_Maybe<Pace> =>
  toEnum(Pace, value, {
    aliases: {
      [Pace.Slow]: ['s'],
      [Pace.Medium]: ['m', 'md', 'med'],
      [Pace.Fast]: ['f']
    }
  });

/**
 * Transforms the specified value to a `Rate` object.
 *
 * @param value - The value to be transformed.
 * @returns The transformed `Rate` or `undefined`.
 */
export const toRate = (value: unknown): T_Maybe<Rate> => {
  const obj = value ? toJsonObject(parseJson(value)) : undefined;

  if (!obj) {
    return undefined;
  }

  const custom = toBoolean(obj.custom);
  const pace = toPace(obj.pace);
  const numberValue = toNumber(obj.value);

  if (isNil(pace) || isNil(numberValue)) {
    return undefined;
  }

  return {
    custom,
    pace,
    value: clamp(numberValue)
  };
};

/**
 * Transforms the specified value to a `Tempo` enumeration.
 *
 * @param value - The value to be transformed.
 * @returns The transformed `Tempo` enumeration or `undefined`.
 */
export const toTempo = (value: unknown): T_Maybe<Tempo> =>
  toEnum(Tempo, value, {
    aliases: {
      [Tempo.Fixed]: ['f', 'fix'],
      [Tempo.Variable]: ['v', 'var']
    }
  });
